/**
 * Finance2NavProgress — a thin top bar while a Finance navigation is in flight.
 *
 * Why it cannot use `useNavigation()`: the app mounts a plain <BrowserRouter>,
 * not a data router, so there is no navigation state to read. And Suspense does
 * not cover it either — when the page's chunk is already cached nothing
 * suspends, React renders the next page in the background (router navigations
 * run in a transition) and the old page just stays on screen.
 *
 * What it uses instead: the router writes the new URL to the browser the moment
 * it navigates, but only exposes it through `useLocation()` once the new page
 * has committed. So "the browser's URL differs from the committed location" IS
 * "a navigation is pending", and it ends by itself when the commit lands — no
 * timer, nothing to forget to clear. A navigation that errors unmounts the
 * boundary this lives in; one that never changed the URL (blocked, same page)
 * never differs, so the bar never shows for it.
 *
 * Checked after clicks, key presses (the Tally screen navigates from F-keys)
 * and popstate (browser back/forward), on the next frame so the handler that
 * navigates has run first. Programmatic navigations with no click or key press
 * ahead of them are not detected.
 */

import React from 'react';
import { useHref, useLocation } from 'react-router-dom';
import { LinearProgress } from '@mui/material';

const browserHref = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const Finance2NavProgress: React.FC = () => {
  const location = useLocation();
  const committed = useHref(`${location.pathname}${location.search}${location.hash}`);
  const committedRef = React.useRef(committed);
  const [pending, setPending] = React.useState(false);

  // A commit landed: whatever was pending is now what is on screen.
  React.useEffect(() => {
    committedRef.current = committed;
    setPending(browserHref() !== committed);
  }, [committed]);

  React.useEffect(() => {
    let frame = 0;
    const check = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setPending(browserHref() !== committedRef.current));
    };
    document.addEventListener('click', check);
    document.addEventListener('keydown', check);
    window.addEventListener('popstate', check);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('click', check);
      document.removeEventListener('keydown', check);
      window.removeEventListener('popstate', check);
    };
  }, []);

  if (!pending) return null;
  return (
    <LinearProgress
      aria-label="Loading page"
      sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 9999, pointerEvents: 'none' }}
    />
  );
};

export default Finance2NavProgress;
